// components/portal/useSessionMessages.ts
import { useCallback, useMemo } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import {
  buildTimelineSessionFeedKey,
  buildTimelineSubjectFeedKey,
  TIMELINE_FEED_LOAD_LIMIT,
} from '@/kernel/useInventory';
import { parseTimelineMessagePayload } from '#lib/leasing/timeline-events';
import { resolveRegistryModelIdFromRuntimeName, STUDIO_MODEL_REGISTRY } from '#lib/model-registry';
import { isEntityFocusSubject } from '#lib/leasing/session-focus';
import type {
  ActiveEntityFocus,
  ChatTimelineViewMode,
  ChatSessionInventoryEntityState,
  Message,
  TimelineEventDTO,
} from '#types/types';

type TimelineEntityNameById = Record<string, string | null | undefined>;

type TimelineMessageEventSnapshot = Pick<
  TimelineEventDTO,
  | 'id'
  | 'category'
  | 'eventType'
  | 'payload'
  | 'createdAt'
  | 'targetEntityType'
  | 'targetEntityId'
  | 'chatSessionId'
>;

type TimelineEntityNameRegistry = Record<string, { name?: unknown } | undefined>;

type TimelineStudioEntityIds = {
  workspaceIds: string[];
  layerIds: string[];
};

const MAX_TIMELINE_SYSTEM_MESSAGE_LENGTH = 220;
const STRUCTURED_MUTATION_EVENT_TYPES = new Set([
  'STUDIO_GENERATION_TASK_SPAWNED',
  'STUDIO_LAYER_MERGED',
  'STUDIO_LAYER_CUT_VALIDATED',
]);
// Nothing agentic renders as a chat message any more. Anchoring is BOOKKEEPING, the agent
// recording which entity it is holding in context, and printing it produced lines like
// "Context anchored (Property:cmldnhzv20001v9eko4qcpr5j)" in the middle of a conversation: a raw
// cuid, in front of the user, saying nothing they can act on. SUBJECT_FOCUSED and SUBJECT_CLEARED
// were already hidden for the same reason; this just finishes the job. The events still exist and
// still feed the audit timeline, they are simply not speech.
const STRUCTURED_AGENTIC_EVENT_TYPES = new Set<string>([]);
const HIDDEN_AGENTIC_EVENT_TYPES = new Set([
  'SUBJECT_FOCUSED',
  'SUBJECT_CLEARED',
  'ENTITY_ANCHORED',
  'ENTITY_UNANCHORED',
]);
const HIDDEN_MUTATION_TARGET_TYPES = new Set(['TimelineEvent', 'ChatSession', 'User']);
/**
 * The machine's own commentary on a run: "Nano Banana 2 launched : Golden Hour" and "Merged X into
 * Y". Noise next to the command trace and the agent's own words, which already say what started:
 * the owner had them removed from List on 2026-08-25 and from the canvas on 2026-09-28.
 */
const STUDIO_PLUMBING_EVENT_TYPES = new Set(['STUDIO_GENERATION_TASK_SPAWNED', 'STUDIO_LAYER_MERGED']);
const LOW_SIGNAL_MUTATION_UPDATE_FIELDS = new Set([
  'updatedAt',
  'lastActive',
  'activeSubject',
  'subjectHistory',
  'contextSummary',
]);
export { TIMELINE_FEED_LOAD_LIMIT };
const EMPTY_TIMELINE_EVENT_IDS: string[] = [];
const EMPTY_TIMELINE_EVENT_SNAPSHOTS: TimelineMessageEventSnapshot[] = [];
const EMPTY_TIMELINE_ENTITY_NAME_LOOKUP: TimelineEntityNameById = {};
type TimelineUILanguage = 'en' | 'fr';
const TIMELINE_UI_TRANSLATIONS: Record<
  TimelineUILanguage,
  {
    quickEnhance: string;
    quickUpscale: string;
    imageGeneration: string;
    videoGeneration: string;
    autoCut: string;
    generation: string;
    launched: string;
    onWorkspace: string;
    layerFallback: string;
    merged: string;
    into: string;
    cutValidated: string;
  }
> = {
  en: {
    quickEnhance: 'Quick enhance',
    quickUpscale: 'Quick upscale',
    imageGeneration: 'Image generation',
    videoGeneration: 'Video generation',
    autoCut: 'Auto cut',
    generation: 'Generation',
    launched: 'launched',
    onWorkspace: 'on workspace',
    layerFallback: 'layer',
    merged: 'Merged',
    into: 'into',
    cutValidated: 'Cut validated',
  },
  fr: {
    quickEnhance: 'Amelioration rapide',
    quickUpscale: 'Upscale rapide',
    imageGeneration: 'Generation image',
    videoGeneration: 'Generation video',
    autoCut: 'Decoupe auto',
    generation: 'Generation',
    launched: 'lance',
    onWorkspace: 'sur workspace',
    layerFallback: 'calque',
    merged: 'Fusionne',
    into: 'dans',
    cutValidated: 'Decoupe validee',
  },
};

const buildTimelineSubjectKey = (
  targetEntityType: string,
  targetEntityId: string
): string => `${targetEntityType}:${targetEntityId}`;

const getMessageTimelineEventId = (message: Message): string =>
  typeof message.timelineEventId === 'string' ? message.timelineEventId.trim() : '';

const getMessageFallbackKey = (message: Message): string =>
  `${message.role}:${String(message.timestamp ?? '')}`;

const toMessageContentString = (content: Message['content']): string => {
  if (Array.isArray(content)) {
    return content.join('\n');
  }
  return typeof content === 'string' ? content : String(content ?? '');
};

const LEGACY_GENERATION_NOISE_PATTERNS: RegExp[] = [
  /^\d+\s+layers?\s+generated$/i,
  /^\[\s*\d+\s*\|\s*fater-[a-z0-9-_.]+\s*\]$/i,
  /^\[\s*\d+\s*\|\s*Result\s+\d{1,2}:\d{2}:\d{2}(?:\s*[AP]M)?\s*\|\s*[a-z0-9-_.]+\s*\]$/i,
];

const isLegacyGenerationNoiseMessage = (message: Message): boolean => {
  const role = normalizeNonEmptyString(message.role)?.toLowerCase();
  if (role !== 'system') return false;
  const content = toMessageContentString(message.content).trim();
  if (!content) return false;
  return LEGACY_GENERATION_NOISE_PATTERNS.some((pattern) => pattern.test(content));
};

const mergeMessageWithOverlay = (base: Message, overlay: Message): Message => {
  const baseTimelineEventId = getMessageTimelineEventId(base);
  const overlayTimelineEventId = getMessageTimelineEventId(overlay);
  const merged: Message = {
    ...base,
    ...overlay,
  };

  if (baseTimelineEventId) {
    merged.timelineEventId = baseTimelineEventId;
  } else if (overlayTimelineEventId) {
    merged.timelineEventId = overlayTimelineEventId;
  }

  return merged;
};

const readPendingMessages = (
  session: ChatSessionInventoryEntityState | null
): Message[] => {
  if (!session) return [];

  const baseMessages = Array.isArray(session.pendingMessages)
    ? session.pendingMessages
    : Array.isArray(session.runtime?.pendingMessages)
      ? session.runtime.pendingMessages
      : [];

  return baseMessages.filter((message) => {
    if (!message) return false;
    const timelineEventId = getMessageTimelineEventId(message);
    if (!timelineEventId && normalizeNonEmptyString(message.role)?.toLowerCase() === 'system') {
      return false;
    }
    if (isLegacyGenerationNoiseMessage(message)) {
      return false;
    }
    return true;
  });
};

const toSortableTimestamp = (value: string | null | undefined): number => {
  if (typeof value !== 'string') return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const clampTimelineMessage = (value: string): string => {
  if (value.length <= MAX_TIMELINE_SYSTEM_MESSAGE_LENGTH) return value;
  return `${value.slice(0, MAX_TIMELINE_SYSTEM_MESSAGE_LENGTH - 3)}...`;
};

const shouldHideMutationEvent = (
  event: Pick<TimelineEventDTO, 'targetEntityType' | 'eventType'>,
  payload: Record<string, unknown>
): boolean => {
  const targetEntityType = normalizeNonEmptyString(event.targetEntityType);
  if (targetEntityType && HIDDEN_MUTATION_TARGET_TYPES.has(targetEntityType)) {
    return true;
  }

  const upperEventType = (normalizeNonEmptyString(event.eventType) ?? '').toUpperCase();
  if (upperEventType !== 'UPDATE') return false;

  const payloadKeys = Object.keys(payload);
  if (payloadKeys.length === 0) return false;
  return payloadKeys.every((key) => LOW_SIGNAL_MUTATION_UPDATE_FIELDS.has(key));
};

const toTimelineMessageEventSnapshot = (event: TimelineEventDTO): TimelineMessageEventSnapshot => ({
  id: event.id,
  category: event.category,
  eventType: event.eventType,
  payload: event.payload,
  createdAt: event.createdAt,
  targetEntityType: event.targetEntityType,
  targetEntityId: event.targetEntityId,
  chatSessionId: event.chatSessionId,
});

// Built in a useMemo keyed on the registry reference, NOT serialized inside a store
// selector. The previous form JSON.stringify'd the whole transcript on every store write
// (of any slice) to fake structural equality, which dominated render cost in long sessions.
const buildTimelineEventSnapshots = (
  timelineIds: string[],
  timelineRegistry: Record<string, TimelineEventDTO>
): TimelineMessageEventSnapshot[] => {
  const snapshots: TimelineMessageEventSnapshot[] = [];
  timelineIds.forEach((timelineEventId) => {
    const event = timelineRegistry[timelineEventId];
    if (event && normalizeNonEmptyString(event.id)) {
      snapshots.push(toTimelineMessageEventSnapshot(event));
    }
  });
  return snapshots.length > 0 ? snapshots : EMPTY_TIMELINE_EVENT_SNAPSHOTS;
};

const addUniqueTimelineEntityId = (ids: string[], seen: Set<string>, value: unknown): void => {
  const id = normalizeNonEmptyString(value);
  if (!id || seen.has(id)) return;
  seen.add(id);
  ids.push(id);
};

const collectTimelineStudioEntityIds = (
  timelineEvents: TimelineMessageEventSnapshot[]
): TimelineStudioEntityIds => {
  const workspaceIds: string[] = [];
  const layerIds: string[] = [];
  const seenWorkspaceIds = new Set<string>();
  const seenLayerIds = new Set<string>();

  timelineEvents.forEach((event) => {
    const payload = toRecord(event.payload) ?? {};
    addUniqueTimelineEntityId(workspaceIds, seenWorkspaceIds, payload.workspaceId);
    addUniqueTimelineEntityId(layerIds, seenLayerIds, payload.layerId);
    addUniqueTimelineEntityId(layerIds, seenLayerIds, payload.sourceLayerId);
    addUniqueTimelineEntityId(layerIds, seenLayerIds, payload.targetLayerId);

    if (event.targetEntityType === 'StudioWorkspace') {
      addUniqueTimelineEntityId(workspaceIds, seenWorkspaceIds, event.targetEntityId);
    } else if (event.targetEntityType === 'StudioLayer') {
      addUniqueTimelineEntityId(layerIds, seenLayerIds, event.targetEntityId);
    }
  });

  return { workspaceIds, layerIds };
};

const buildTimelineEntityNameLookup = (
  ids: string[],
  registry: TimelineEntityNameRegistry
): TimelineEntityNameById => {
  if (ids.length === 0) return EMPTY_TIMELINE_ENTITY_NAME_LOOKUP;
  return ids.reduce<TimelineEntityNameById>((acc, id) => {
    acc[id] = normalizeNonEmptyString(registry[id]?.name);
    return acc;
  }, {});
};

const resolveGenerationSpawnWorkspaceName = (
  payload: Record<string, unknown>,
  workspaceNameById: TimelineEntityNameById
): string | null => {
  const workspaceId = normalizeNonEmptyString(payload.workspaceId);
  const name = normalizeNonEmptyString(payload.workspaceName)
    ?? (workspaceId ? normalizeNonEmptyString(workspaceNameById[workspaceId]) : null);
  // An unnamed workspace is named after its own position ("1", "2", …) or, on some paths,
  // after its id outright (an 8-hex slice). Reading "on workspace a3f9c1d2" back to someone
  // who has never seen a workspace is internal plumbing, not telemetry. Mention it only when
  // a human actually named it, which is why this compares against the id rather than just
  // rejecting digits: a workspace deliberately named "2024" survives.
  if (!name) return null;
  if (workspaceId && name === workspaceId) return null;
  if (/^\d{1,3}$/.test(name)) return null;   // the positional default, never a chosen name
  return name;
};

const resolveGenerationSpawnLayerName = (
  payload: Record<string, unknown>,
  layerNameById: TimelineEntityNameById
): string | null => {
  const layerId = normalizeNonEmptyString(payload.layerId);
  if (!layerId) return null;
  return normalizeNonEmptyString(layerNameById[layerId]);
};

const resolveLayerName = (
  payload: Record<string, unknown>,
  layerNameById: TimelineEntityNameById,
  {
    idKey = 'layerId',
    nameKey = 'layerName',
  }: { idKey?: string; nameKey?: string } = {}
): string | null => {
  const providedName = normalizeNonEmptyString(payload[nameKey]);
  if (providedName) return providedName;
  const layerId = normalizeNonEmptyString(payload[idKey]);
  if (!layerId) return null;
  return normalizeNonEmptyString(layerNameById[layerId]);
};

const resolveGenerationSpawnActionLabel = (
  payload: Record<string, unknown>,
  lang: TimelineUILanguage
): string => {
  const t = TIMELINE_UI_TRANSLATIONS[lang];
  const model = normalizeNonEmptyString(payload.model);
  if (model === 'fater-enhance') return t.quickEnhance;
  if (model === 'fater-upscale') return t.quickUpscale;

  const taskType = normalizeNonEmptyString(payload.taskType);
  if (taskType === 'diffuse') return t.imageGeneration;
  if (taskType === 'film') return t.videoGeneration;
  if (taskType === 'autocut') return t.autoCut;
  if (taskType) return taskType;

  return t.generation;
};

const resolveGenerationSpawnModelLabel = (payload: Record<string, unknown>): string | null => {
  const model = normalizeNonEmptyString(payload.model);
  if (!model) return null;
  const registryId = resolveRegistryModelIdFromRuntimeName(model) ?? model;
  const definition = STUDIO_MODEL_REGISTRY[registryId as keyof typeof STUDIO_MODEL_REGISTRY];
  if (definition && typeof definition.name === 'string' && definition.name.trim().length > 0) {
    return definition.name.trim();
  }
  return model;
};

const formatGenerationSpawnContent = (
  modelLabel: string | null,
  actionLabel: string,
  workspaceName: string | null,
  layerName: string | null,
  prompt: string | null,
  lang: TimelineUILanguage
): string => {
  const t = TIMELINE_UI_TRANSLATIONS[lang];
  const normalizedAction = normalizeNonEmptyString(modelLabel)
    ?? normalizeNonEmptyString(actionLabel)
    ?? t.generation;
  let line = `${normalizedAction} ${t.launched}`;
  if (workspaceName && layerName) {
    line = `${line} ${t.onWorkspace} ${workspaceName} : ${layerName}`;
  } else if (workspaceName) {
    line = `${line} ${t.onWorkspace} ${workspaceName}`;
  } else if (layerName) {
    line = `${line} : ${layerName}`;
  }
  if (prompt) {
    line = `${line}\n\n<model_prompt label="Submitted prompt">\n${prompt}\n</model_prompt>`;
  }
  return line;
};

const timelineEventToMessage = (
  event: TimelineMessageEventSnapshot,
  workspaceNameById: TimelineEntityNameById,
  layerNameById: TimelineEntityNameById,
  lang: TimelineUILanguage,
): Message | null => {
  const t = TIMELINE_UI_TRANSLATIONS[lang];
  if (event.category === 'TELEMETRY') return null;

  const eventType = normalizeNonEmptyString(event.eventType) ?? 'UNKNOWN_EVENT';
  const eventTypeUpper = eventType.toUpperCase();
  if (STUDIO_PLUMBING_EVENT_TYPES.has(eventTypeUpper)) {
    return null;
  }
  if (event.category === 'AGENTIC' && HIDDEN_AGENTIC_EVENT_TYPES.has(eventTypeUpper)) {
    return null;
  }
  if (event.category === 'AGENTIC' && !STRUCTURED_AGENTIC_EVENT_TYPES.has(eventTypeUpper)) {
    return null;
  }

  if (event.category === 'COMMUNICATION') {
    const parsed = parseTimelineMessagePayload(event.payload);
    if (!parsed) return null;
    if (isLegacyGenerationNoiseMessage(parsed as Message)) return null;
    return {
      ...parsed,
      timelineEventId: event.id,
    } as Message;
  }

  const payload = toRecord(event.payload) ?? {};
  if (event.category === 'MUTATION' && shouldHideMutationEvent(event, payload)) {
    return null;
  }
  if (event.category === 'MUTATION' && !STRUCTURED_MUTATION_EVENT_TYPES.has(eventTypeUpper)) {
    return null;
  }

  let content: string;
  if (eventTypeUpper === 'STUDIO_GENERATION_TASK_SPAWNED') {
    const modelLabel = resolveGenerationSpawnModelLabel(payload);
    const actionLabel = resolveGenerationSpawnActionLabel(payload, lang);
    const workspaceName = resolveGenerationSpawnWorkspaceName(payload, workspaceNameById);
    const layerName = resolveGenerationSpawnLayerName(payload, layerNameById);
    const prompt = normalizeNonEmptyString(payload.prompt);
    const content = formatGenerationSpawnContent(
      modelLabel,
      actionLabel,
      workspaceName,
      layerName,
      prompt,
      lang
    );
    return {
      role: 'system',
      content,
      timestamp: normalizeNonEmptyString(event.createdAt) ?? new Date().toISOString(),
      timelineEventId: event.id,
      agentName: 'generation-spawn',
      ...(normalizeNonEmptyString(event.targetEntityType) && normalizeNonEmptyString(event.targetEntityId)
        ? {
            targetEntityType: normalizeNonEmptyString(event.targetEntityType) as string,
            targetEntityId: normalizeNonEmptyString(event.targetEntityId) as string,
          }
        : {}),
    };
  } else if (eventTypeUpper === 'STUDIO_LAYER_MERGED') {
    const workspaceName = resolveGenerationSpawnWorkspaceName(payload, workspaceNameById);
    const sourceLayerName =
      resolveLayerName(payload, layerNameById, { idKey: 'sourceLayerId', nameKey: 'sourceLayerName' })
      ?? t.layerFallback;
    const targetLayerName =
      resolveLayerName(payload, layerNameById, { idKey: 'targetLayerId', nameKey: 'targetLayerName' })
      ?? t.layerFallback;
    content = workspaceName
      ? `${t.merged} "${sourceLayerName}" ${t.into} "${targetLayerName}" ${t.onWorkspace} ${workspaceName}`
      : `${t.merged} "${sourceLayerName}" ${t.into} "${targetLayerName}"`;
  } else if (eventTypeUpper === 'STUDIO_LAYER_CUT_VALIDATED') {
    const workspaceName = resolveGenerationSpawnWorkspaceName(payload, workspaceNameById);
    const layerName = resolveLayerName(payload, layerNameById) ?? t.layerFallback;
    content = workspaceName
      ? `${t.cutValidated} ${t.onWorkspace} ${workspaceName} : ${layerName}`
      : `${t.cutValidated} : ${layerName}`;
  } else {
    return null;
  }

  const targetEntityType = normalizeNonEmptyString(event.targetEntityType);
  const targetEntityId = normalizeNonEmptyString(event.targetEntityId);
  return {
    role: 'system',
    content: clampTimelineMessage(content),
    timestamp: normalizeNonEmptyString(event.createdAt) ?? new Date().toISOString(),
    timelineEventId: event.id,
    ...(targetEntityType && targetEntityId
      ? {
          targetEntityType,
          targetEntityId,
        }
      : {}),
  };
};

const listTimelineMessagesForFeed = (
  timelineEvents: TimelineMessageEventSnapshot[],
  workspaceNameById: TimelineEntityNameById,
  layerNameById: TimelineEntityNameById,
  lang: TimelineUILanguage,
): Message[] => {
  return timelineEvents
    .filter((event) => event.category !== 'TELEMETRY')
    .sort((left, right) => {
      const leftTs = toSortableTimestamp(left.createdAt);
      const rightTs = toSortableTimestamp(right.createdAt);
      if (leftTs === rightTs) return left.id.localeCompare(right.id);
      return leftTs - rightTs;
    })
    .map((event) => timelineEventToMessage(
      event,
      workspaceNameById,
      layerNameById,
      lang,
    ))
    .filter((message): message is Message => Boolean(message));
};

const mergeTimelineAndPendingMessages = (
  timelineMessages: Message[],
  pendingMessages: Message[]
): Message[] => {
  const mergedMessages = [...timelineMessages];
  const indexByTimelineEventId = new Map<string, number>();
  const indexByFallbackKey = new Map<string, number>();

  mergedMessages.forEach((message, index) => {
    const timelineEventId = getMessageTimelineEventId(message);
    if (timelineEventId) {
      indexByTimelineEventId.set(timelineEventId, index);
    }
    indexByFallbackKey.set(getMessageFallbackKey(message), index);
  });

  pendingMessages.forEach((message) => {
    const timelineEventId = getMessageTimelineEventId(message);
    const fallbackKey = getMessageFallbackKey(message);

    const existingIndexByTimelineEventId =
      timelineEventId && indexByTimelineEventId.has(timelineEventId)
        ? indexByTimelineEventId.get(timelineEventId)
        : undefined;
    const existingIndexByFallback =
      indexByFallbackKey.has(fallbackKey)
        ? indexByFallbackKey.get(fallbackKey)
        : undefined;
    const existingIndex =
      typeof existingIndexByTimelineEventId === 'number'
        ? existingIndexByTimelineEventId
        : existingIndexByFallback;

    if (typeof existingIndex === 'number' && mergedMessages[existingIndex]) {
      const merged = mergeMessageWithOverlay(mergedMessages[existingIndex]!, message);
      mergedMessages[existingIndex] = merged;
      const mergedTimelineEventId = getMessageTimelineEventId(merged);
      if (mergedTimelineEventId) {
        indexByTimelineEventId.set(mergedTimelineEventId, existingIndex);
      }
      indexByFallbackKey.set(getMessageFallbackKey(merged), existingIndex);
      return;
    }

    const nextIndex = mergedMessages.length;
    mergedMessages.push(message);
    if (timelineEventId) {
      indexByTimelineEventId.set(timelineEventId, nextIndex);
    }
    indexByFallbackKey.set(fallbackKey, nextIndex);
  });

  mergedMessages.sort(
    (left, right) =>
      toSortableTimestamp(left.timestamp) - toSortableTimestamp(right.timestamp)
  );
  return mergedMessages;
};

type TimelineFeedScope = ChatTimelineViewMode;

interface UseSessionMessagesOptions {
  scope?: TimelineFeedScope;
  subject?: ActiveEntityFocus | null;
}

const resolveSubjectKeyFromFocus = (subject: ActiveEntityFocus | null | undefined): string | null => {
  const normalizedSubject = subject ?? null;
  if (!isEntityFocusSubject(normalizedSubject)) return null;
  const entityType = normalizeNonEmptyString(normalizedSubject.entityType);
  const entityId = normalizeNonEmptyString(normalizedSubject.entityId);
  if (!entityType || !entityId) return null;
  return buildTimelineSubjectKey(entityType, entityId);
};

const resolveFeedScope = (
  requestedScope: TimelineFeedScope,
  subjectKey: string | null
): TimelineFeedScope => {
  if (requestedScope === 'subject' && subjectKey) return 'subject';
  return 'session';
};

export const useSessionMessages = (
  sessionId: string | null | undefined,
  options?: UseSessionMessagesOptions
): Message[] => {
  const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
  const requestedScope: TimelineFeedScope = options?.scope === 'subject' ? 'subject' : 'session';
  const subjectKey = useMemo(
    () => resolveSubjectKeyFromFocus(options?.subject),
    [options?.subject?.entityType, options?.subject?.entityId]
  );
  const feedScope = useMemo(
    () => resolveFeedScope(requestedScope, subjectKey),
    [requestedScope, subjectKey]
  );
  const session = useKernelStore((state) =>
    normalizedSessionId
      ? state.inventory.ChatSession.registry[normalizedSessionId] ?? null
      : null
  );
  const lang = useKernelStore((state) => state.language);
  const timelineIds = useKernelStore((state) =>
    normalizedSessionId
      ? feedScope === 'subject' && subjectKey
        ? (state.timelineFeedIndex.bySubjectKey[subjectKey] ?? EMPTY_TIMELINE_EVENT_IDS)
        : (state.timelineFeedIndex.bySessionId[normalizedSessionId] ?? EMPTY_TIMELINE_EVENT_IDS)
      : EMPTY_TIMELINE_EVENT_IDS
  );
  // Direct Store Access: each selector returns a stable registry reference, so no work
  // runs per store write; the derivations below recompute only when the entities they
  // actually read have changed.
  const timelineRegistry = useKernelStore((state) => state.inventory.TimelineEvent.registry);
  const timelineEvents = useMemo(
    () => buildTimelineEventSnapshots(timelineIds, timelineRegistry),
    [timelineIds, timelineRegistry]
  );
  const studioEntityIds = useMemo(
    () => collectTimelineStudioEntityIds(timelineEvents),
    [timelineEvents]
  );
  const workspaceRegistry = useKernelStore((state) => state.inventory.StudioWorkspace.registry);
  const layerRegistry = useKernelStore((state) => state.inventory.StudioLayer.registry);
  const workspaceNameById = useMemo(
    () => buildTimelineEntityNameLookup(studioEntityIds.workspaceIds, workspaceRegistry),
    [studioEntityIds.workspaceIds, workspaceRegistry]
  );
  const layerNameById = useMemo(
    () => buildTimelineEntityNameLookup(studioEntityIds.layerIds, layerRegistry),
    [studioEntityIds.layerIds, layerRegistry]
  );

  return useMemo(() => {
    if (!normalizedSessionId || !session) return [];
    const timelineMessages = listTimelineMessagesForFeed(
      timelineEvents,
      workspaceNameById,
      layerNameById,
      lang,
    );
    if (feedScope === 'subject') {
      return timelineMessages;
    }
    const pendingMessages = readPendingMessages(session);
    return mergeTimelineAndPendingMessages(timelineMessages, pendingMessages);
  }, [normalizedSessionId, session, feedScope, timelineEvents, workspaceNameById, layerNameById, lang]);
};

interface UseTimelineFeedMessagesResult {
  messages: Message[];
  scope: TimelineFeedScope;
  hasMoreOlder: boolean;
  isLoadingOlder: boolean;
  loadOlder: () => Promise<void>;
}

export const useTimelineFeedMessages = (
  sessionId: string | null | undefined,
  options?: UseSessionMessagesOptions
): UseTimelineFeedMessagesResult => {
  const { kernel } = useKernel();
  const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
  const requestedScope: TimelineFeedScope = options?.scope === 'subject' ? 'subject' : 'session';
  const subjectKey = useMemo(
    () => resolveSubjectKeyFromFocus(options?.subject),
    [options?.subject?.entityType, options?.subject?.entityId]
  );
  const feedScope = useMemo(
    () => resolveFeedScope(requestedScope, subjectKey),
    [requestedScope, subjectKey]
  );
  const messages = useSessionMessages(normalizedSessionId, {
    scope: feedScope,
    subject: options?.subject ?? null,
  });
  const feedKey = useMemo(() => {
    if (!normalizedSessionId) return null;
    if (feedScope === 'subject' && subjectKey) {
      return buildTimelineSubjectFeedKey(subjectKey);
    }
    return buildTimelineSessionFeedKey(normalizedSessionId);
  }, [normalizedSessionId, feedScope, subjectKey]);
  const feedState = useKernelStore((state) =>
    feedKey ? state.timelineFeedState[feedKey] ?? null : null
  );

  // Only advertise "more older" when there is actually a cursor to load from. A stale
  // hasMoreOlder=true with no cursor would show a "Load older events" button that does
  // nothing (loadOlder bails without a cursor), which is exactly the "nothing to load" bug.
  const hasMoreOlder =
    feedState?.hasMoreOlder === true && !!normalizeNonEmptyString(feedState?.olderCursor);
  const isLoadingOlder = feedState?.isLoading === true;

  const loadOlder = useCallback(async () => {
    if (!kernel.current || !normalizedSessionId) return;
    await kernel.current.loadOlderTimelineEvents({
      sessionId: normalizedSessionId,
      scope: feedScope,
      subject: options?.subject ?? null,
    });
  }, [kernel, normalizedSessionId, feedScope, options?.subject]);

  return {
    messages,
    scope: feedScope,
    hasMoreOlder,
    isLoadingOlder,
    loadOlder,
  };
};
